import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useStore, type StoreApi } from 'zustand';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Info,
  List,
  Loader2,
  Lock,
  OctagonAlert,
  Package,
  RotateCcw,
  SearchX,
  Send,
  TriangleAlert,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DebouncedSearchInput } from '@/components/ui/debounced-search-input';
import { EmptyState } from '@/components/ui/empty-state';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/workspace-dialog';
import { cn } from '@/lib/utils';
import type { NetSuiteEmployeeDto } from '@features/users/types';
import { NetSuiteEmployeePicker } from '../NetSuiteEmployeePicker';
import { isInsideMrsHoverCard, isMrsHoverCardOpen } from '../mrs-hover-card';
import { materialRequestService, picklistService } from '../../services';
import { usePicklistStore } from '../../store';
import type { AdjustmentReasonDto, PicklistDto, ReleasedQuantityDto } from '../../types';
import { buildPostPayload, rowQuantity } from './postPayload';
import { createPostDialogStore, parseQty, parseReading, type GroupBy, type PostDialogState } from './postDialogStore';
import {
  aboutToPost,
  buildPostRows,
  lockedButton,
  lockedLines,
  lockedNote,
  matchesLineQuery,
  mixedState,
  overBy,
  overDetail,
  overNote,
  postOnlyOptions,
  qtyError,
  readingError,
  readingSeeds,
  requestorNote,
  rowFigures,
  type PostRow,
  type RowFigures,
  type Stat,
} from './postRows';
import { MrsLineDetailsPopover } from './MrsLineDetailsPopover';
import { PostLineRow } from './PostLineRow';
import { PostSerialsSheet } from './PostSerialsSheet';

interface PostToMisDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  picklist: PicklistDto;
}

/**
 * PT-5 — Post to MIS v2, rebuilt from the prototype (L1504-1676) as its own components.
 *
 * Workspace-scoped (it lives in the content column; the rail stays visible). The card's own width decides the
 * layout through two CSS containers (`globals.css`, `.post-card` / `.post-lines`): wide above 1000px with the
 * details column on the right, stacked below it; rows compact below an 880px lines column.
 *
 * "Post to NetSuite" runs the claim directly: there is no second "Confirm Post to NetSuite" dialog (owner ruling
 * 2026-09-30, as the design draws it). The "About to post" summary sits right above the button.
 */
export function PostToMisDialog({ open, onOpenChange, picklist }: PostToMisDialogProps) {
  // S3 — PT-7's hover card is portaled outside this dialog's content, so a press on it reads as "outside" and
  // would close the dialog with everything typed; an Escape meant for the card would close the dialog too. The
  // guard only PREVENTS the default: the card's own Escape listener still closes the card.
  const guardOutside = (e: Event) => {
    if (isInsideMrsHoverCard(e.target)) e.preventDefault();
  };
  const guardEscape = (e: KeyboardEvent) => {
    if (isMrsHoverCardOpen()) e.preventDefault();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        overlayClassName="bg-black/50"
        onPointerDownOutside={guardOutside}
        onInteractOutside={guardOutside}
        onEscapeKeyDown={guardEscape}
        className="post-card block h-[calc(100%-48px)] max-h-none w-[calc(100%-48px)] max-w-none gap-0 rounded-[14px] bg-card p-0 sm:rounded-[14px]"
      >
        {/* Mounted only while open: the body's store — and its ClientKey — live exactly as long as the dialog. */}
        {open && <PostBody picklist={picklist} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

// ── Data the body loads on open, in PARALLEL (UI Performance gate rule 3) ──────────────────────────────────

let reasonsCache: AdjustmentReasonDto[] | null = null;

function useAdjustmentReasons() {
  const [state, setState] = useState({ reasons: reasonsCache ?? [], loading: reasonsCache === null });
  useEffect(() => {
    if (reasonsCache) return;
    let live = true;
    picklistService.getAdjustmentReasons()
      .then((reasons) => {
        reasonsCache = reasons;
        if (live) setState({ reasons, loading: false });
      })
      .catch(() => {
        if (live) setState((s) => ({ ...s, loading: false }));
        toast.error('Failed to load adjustment reasons');
      });
    return () => { live = false; };
  }, []);
  return state;
}

type ReleasedStatus = 'loading' | 'loaded' | 'failed' | 'forbidden';

function useReleasedFigures(materialRequestItemIds: readonly number[]) {
  const [state, setState] = useState<{ status: ReleasedStatus; map: Map<number, ReleasedQuantityDto> }>(() => ({
    status: materialRequestItemIds.length > 0 ? 'loading' : 'loaded',
    map: new Map(),
  }));

  const load = useCallback(async () => {
    if (materialRequestItemIds.length === 0) return;
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const results = await materialRequestService.getReleasedQuantities([...materialRequestItemIds]);
      setState({ status: 'loaded', map: new Map(results.map((r) => [r.materialRequestItemId, r])) });
    } catch (error) {
      // Branch on the status BEFORE a bare catch collapses both cases into one banner: a 403 is a permission,
      // nothing to retry; anything else can be retried in place.
      const status = (error as { response?: { status?: number } })?.response?.status;
      setState((s) => ({ status: status === 403 ? 'forbidden' : 'failed', map: s.map }));
    }
  }, [materialRequestItemIds]);

  useEffect(() => { void load(); }, [load]);
  return { ...state, retry: load };
}

/** The MRS endorser as the Requested-by picker's value (today's auto-seed, :838–879). */
function employeeFromRow(row: PostRow): NetSuiteEmployeeDto {
  const full = (row.requestorFullName ?? '').trim();
  const comma = full.indexOf(',');
  const lastName = comma >= 0 ? full.slice(0, comma).trim() : full;
  const rest = comma >= 0 ? full.slice(comma + 1).trim().split(/\s+/) : [];
  return {
    id: String(row.accountRequestorId),
    entityId: '',
    firstName: rest[0] ?? '',
    middleName: rest.length > 1 ? rest.slice(1).join(' ') : null,
    lastName,
    email: null,
    hireDate: null,
    birthdate: null,
    employeeIdNumber: null,
    departmentId: null,
    departmentName: null,
    positionId: null,
    positionName: null,
  };
}

// ── The body ──────────────────────────────────────────────────────────────────────────────────────────────

function PostBody({ picklist, onClose }: { picklist: PicklistDto; onClose: () => void }) {
  const rows = buildPostRows(picklist);
  const seeds = readingSeeds(picklist);
  // Once per open. A detail refresh (the page polls while a batch is in flight) rebuilds `rows` but never the
  // store, so every typed value and the ClientKey survive it.
  const [store] = useState(() => createPostDialogStore({ rows, readingSeeds: seeds }));
  const [mrsLineIds] = useState(() => [...new Set(rows.map((r) => r.materialRequestItemId))]);

  const reasons = useAdjustmentReasons();
  const released = useReleasedFigures(mrsLineIds);
  const lookupFailed = released.status === 'failed' || released.status === 'forbidden';
  const figures = new Map(rows.map((r) => [r.id, rowFigures(r, released.map.get(r.materialRequestItemId))] as const));

  const [reasonId, setReasonId] = useState(0);
  const [chosenRequestedBy, setChosenRequestedBy] = useState<NetSuiteEmployeeDto | null>(null);
  const [requestedByTouched, setRequestedByTouched] = useState(false);
  const [serialsRowId, setSerialsRowId] = useState<number | null>(null);
  const [details, setDetails] = useState<{ rowId: number; anchor: HTMLElement } | null>(null);

  const openSerials = useCallback((row: PostRow) => setSerialsRowId(row.id), []);
  const openDetails = useCallback((row: PostRow, anchor: HTMLElement) => setDetails({ rowId: row.id, anchor }), []);
  const postOnly = (rowIds: readonly number[]) => {
    store.getState().onlyRows(rowIds);
    // The prototype's onlyEndorser: a typed Requested by is discarded and re-seeds from the new selection.
    setChosenRequestedBy(null);
    setRequestedByTouched(false);
  };

  const serialsRow = rows.find((r) => r.id === serialsRowId) ?? null;
  const detailsRow = details ? rows.find((r) => r.id === details.rowId) ?? null : null;
  const detailsBins = detailsRow ? (picklist.itemGroups ?? []).find((g) => g.id === detailsRow.anchorId)?.bins ?? [] : [];

  return (
    <div className="post-card-inner">
      <div className="post-details">
        <DetailsColumn
          picklist={picklist}
          rows={rows}
          store={store}
          figures={figures}
          releasedStatus={released.status}
          onRetryReleased={() => void released.retry()}
          reasons={reasons.reasons}
          reasonsLoading={reasons.loading}
          reasonId={reasonId}
          onReasonChange={setReasonId}
          chosenRequestedBy={chosenRequestedBy}
          requestedByTouched={requestedByTouched}
          onRequestedByChange={(e) => { setChosenRequestedBy(e); setRequestedByTouched(true); }}
          onClose={onClose}
        />
      </div>

      <LinesSection
        picklist={picklist}
        rows={rows}
        store={store}
        figures={figures}
        lookupFailed={lookupFailed}
        loadingReleased={released.status === 'loading'}
        seeds={seeds}
        onOpenSerials={openSerials}
        onOpenDetails={openDetails}
        onPostOnly={postOnly}
      />

      <PostSerialsSheet row={serialsRow} store={store} onClose={() => setSerialsRowId(null)} />
      <MrsLineDetailsPopover
        target={detailsRow && details ? { row: detailsRow, anchor: details.anchor } : null}
        bins={detailsBins}
        figures={detailsRow ? figures.get(detailsRow.id) ?? null : null}
        onClose={() => setDetails(null)}
      />
    </div>
  );
}

// ── The details column: header, fields, notices, About to post, footer ────────────────────────────────────

interface DetailsColumnProps {
  picklist: PicklistDto;
  rows: PostRow[];
  store: StoreApi<PostDialogState>;
  figures: ReadonlyMap<number, RowFigures>;
  releasedStatus: ReleasedStatus;
  onRetryReleased: () => void;
  reasons: AdjustmentReasonDto[];
  reasonsLoading: boolean;
  reasonId: number;
  onReasonChange: (id: number) => void;
  chosenRequestedBy: NetSuiteEmployeeDto | null;
  requestedByTouched: boolean;
  onRequestedByChange: (e: NetSuiteEmployeeDto | null) => void;
  onClose: () => void;
}

function DetailsColumn({
  picklist,
  rows,
  store,
  figures,
  releasedStatus,
  onRetryReleased,
  reasons,
  reasonsLoading,
  reasonId,
  onReasonChange,
  chosenRequestedBy,
  requestedByTouched,
  onRequestedByChange,
  onClose,
}: DetailsColumnProps) {
  const selected = useStore(store, (s) => s.selected);
  const qtyText = useStore(store, (s) => s.qtyText);
  const ticked = useStore(store, (s) => s.ticked);
  const readingText = useStore(store, (s) => s.readingText);
  const lockedOpen = useStore(store, (s) => s.lockedOpen);
  const [busy, setBusy] = useState(false);

  const tickedSet = new Set(Object.entries(ticked).filter(([, on]) => on).map(([id]) => Number(id)));
  const quantities: Record<number, number> = {};
  for (const r of rows) if (!r.isSerial) quantities[r.id] = parseQty(qtyText[r.id]);

  const selectedRows = rows.filter((r) => selected[r.id]);
  const withQ = selectedRows.map((row) => ({ row, q: rowQuantity(row, quantities, tickedSet) }));
  const overSel = withQ.filter(({ row, q }) => overBy(q, figures.get(row.id)!) > 0);
  const anyQtyError = withQ.some(({ row, q }) => !row.isSerial && qtyError(q, row.max));
  const anyReadingError = selectedRows.some((r) => readingError(readingText[r.anchorId] ?? ''));
  const sending = withQ.filter(({ row, q }) => q > 0 && (row.isSerial || !qtyError(q, row.max)));
  const mixed = mixedState(selectedRows).mixed;
  const locked = lockedLines(picklist);

  // Today's auto-seed, derived: the single endorser of the selection (or of every row when none is selected).
  const seedFrom = selectedRows.length > 0 ? selectedRows : rows;
  const endorsers = [...new Set(seedFrom.map((r) => r.accountRequestorId).filter((id) => id > 0))];
  const requestedBy = requestedByTouched
    ? chosenRequestedBy
    : endorsers.length === 1
      ? employeeFromRow(seedFrom.find((r) => r.accountRequestorId === endorsers[0])!)
      : null;

  const canSubmit = sending.length > 0
    && reasonId > 0
    && requestedBy !== null
    && !mixed
    && !anyQtyError
    && !anyReadingError
    && overSel.length === 0
    && releasedStatus !== 'loading'
    && !busy;

  const submit = async () => {
    if (!canSubmit || !requestedBy) return;
    const s = store.getState();
    const payload = buildPostPayload({
      rows,
      selected: new Set(rows.filter((r) => s.selected[r.id]).map((r) => r.id)),
      quantities,
      ticked: tickedSet,
      memos: s.memos,
      readings: Object.fromEntries(Object.entries(s.readingText).map(([anchor, text]) => [Number(anchor), parseReading(text)])),
    });

    setBusy(true);
    const result = await usePicklistStore.getState().postPicklist(
      payload.items,
      reasonId,
      requestedBy.id,
      // PW-25 — exactly the string the picker shows for this person, so the stored slip names them that way.
      `${requestedBy.lastName}, ${requestedBy.firstName}`.trim(),
      payload.itemMemos,
      payload.itemReadings,
      // E7 — the same key for every press while this dialog stays open.
      s.clientKey,
    );
    setBusy(false);

    if (typeof result === 'number') {
      const n = payload.items.length;
      toast.success('Posting started', {
        description: `Posting ${n} item${n !== 1 ? 's' : ''} to MIS — creating the Inventory Adjustment in NetSuite…`,
        duration: 6000,
      });
      onClose();
    } else {
      // The dialog stays open with everything typed; the next press reuses the key (a 200 replay if the first landed).
      toast.error('Posting Error', { description: result.error, duration: 10000 });
    }
  };

  const loaded = releasedStatus === 'loaded';
  const pillText = releasedStatus === 'loading'
    ? 'MRS released quantities · loading'
    : releasedStatus === 'forbidden'
      ? 'MRS released quantities · not permitted'
      : releasedStatus === 'failed'
        ? 'MRS released quantities · could not load'
        : 'MRS released quantities · loaded';
  const pill = (
    <span
      tabIndex={loaded ? 0 : undefined}
      className={cn(
        'mt-[7px] inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full border px-[9px] text-[10.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        lookupWarning(releasedStatus)
          ? 'border-warning/35 bg-warning/10 text-warning'
          : 'border-success/35 bg-success/10 text-success',
        loaded && 'cursor-help',
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {pillText}
    </span>
  );

  return (
    <>
      <div className="post-head flex items-start justify-between gap-3 px-5 pt-[18px]">
        <div className="min-w-0">
          <DialogTitle className="text-base font-bold">
            Post to MIS <span className="font-mono font-semibold text-muted-foreground">· {picklist.picklistNumber}</span>
          </DialogTitle>
          <DialogDescription className="mt-1 text-[12.5px]">
            Select the picked lines to include in this Material Issuance Slip.
          </DialogDescription>
          {loaded ? (
            <Tooltip>
              <TooltipTrigger asChild>{pill}</TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Live released quantities were read from NetSuite, so the MRS check below is authoritative.
              </TooltipContent>
            </Tooltip>
          ) : (
            pill
          )}
        </div>
        <IconButton label="Close" onClick={onClose} className="h-[30px] w-[30px]">
          <X className="h-[15px] w-[15px]" aria-hidden />
        </IconButton>
      </div>

      <aside aria-label="MIS details" className="post-aside flex min-w-0 flex-col gap-3 overflow-x-hidden px-5 py-4">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-2.5">
          <div>
            <Label htmlFor="post-reason" className="mb-1.5 block text-xs font-semibold">
              Issuance reason <span className="text-destructive">*</span>
            </Label>
            <Select
              value={reasonId > 0 ? String(reasonId) : ''}
              onValueChange={(v) => onReasonChange(Number(v))}
              disabled={reasonsLoading}
            >
              <SelectTrigger id="post-reason" className="h-[34px] rounded-[7px] text-[12.5px]">
                <SelectValue placeholder={reasonsLoading ? 'Loading…' : 'Select a reason…'} />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-semibold">
              Requested by <span className="text-destructive">*</span>
            </Label>
            {/* The picker, not a text box: the payload needs a NetSuite employee id (PTM-14 #5). */}
            <NetSuiteEmployeePicker
              value={requestedBy}
              onChange={onRequestedByChange}
              placeholder="Who receives the materials"
              className="h-[34px] rounded-[7px] text-[12.5px]"
            />
            <p className="mt-1 text-[10.5px] text-muted-foreground">
              Pre-filled with the MRS endorser — change if the actual requestor for this MIS differs.
            </p>
          </div>
        </div>

        {releasedStatus === 'forbidden' && (
          // No Retry on purpose: nothing the user can press changes a permission.
          <Notice tone="warning" icon={TriangleAlert}>
            The MRS figures could not be read because your account does not have the “View request list”
            permission. Posting still works — the server checks the MRS limit before it posts — but the figures
            below will be blank. An administrator can grant it.
          </Notice>
        )}
        {releasedStatus === 'failed' && (
          // O-9 = A: Post stays pressable; the rows fall back to the cached figures and the server still checks.
          <Notice
            tone="warning"
            icon={TriangleAlert}
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={onRetryReleased}
                className="h-7 shrink-0 gap-1.5 border-warning/40 bg-card px-2.5 text-[11.5px] font-semibold text-warning hover:bg-warning/10 hover:text-warning"
              >
                <RotateCcw className="h-3 w-3" aria-hidden />
                Retry
              </Button>
            }
          >
            Could not read the MRS figures from NetSuite, so they are missing below — nothing is wrong with your picks.
          </Notice>
        )}

        {overSel.length > 0 && (
          <Notice tone="destructive" icon={OctagonAlert}>
            <p>{overNote(overSel.length)}</p>
            <div className="mt-[7px] flex flex-wrap gap-[5px]">
              {overSel.map(({ row, q }) => {
                const f = figures.get(row.id)!;
                return (
                  <span key={row.id} className="inline-flex h-[21px] items-center gap-1.5 rounded-md border border-destructive/30 bg-card px-2 text-[11px] font-medium text-foreground">
                    <span className="font-mono font-semibold">{row.itemCode}</span>
                    <span className="tabular-nums text-muted-foreground">{overDetail(q, f.headroom ?? 0, overBy(q, f))}</span>
                  </span>
                );
              })}
            </div>
          </Notice>
        )}

        {locked.length > 0 && (
          <div className="rounded-lg border border-border bg-muted/45 px-[11px] py-[9px]">
            <div className="flex items-start gap-2">
              <Lock className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <div className="min-w-0">
                <p className="text-xs leading-normal text-muted-foreground">{lockedNote(locked.length)}</p>
                <Button
                  variant="outline"
                  size="sm"
                  aria-expanded={lockedOpen}
                  onClick={() => store.getState().toggleLocked()}
                  className="mt-[7px] h-[26px] gap-[5px] bg-card px-[9px] text-[11.5px]"
                >
                  {lockedOpen ? <ChevronUp className="h-[13px] w-[13px]" aria-hidden /> : <ChevronDown className="h-[13px] w-[13px]" aria-hidden />}
                  {lockedButton(locked.length, lockedOpen)}
                </Button>
                {lockedOpen && (
                  <div className="mt-[7px] flex flex-wrap gap-[5px]">
                    {locked.map((l) => (
                      <span key={l.id} className="inline-flex h-[21px] items-center gap-1.5 rounded-md border border-border bg-card px-2 text-[11px] font-medium">
                        <span className="font-mono font-semibold">{l.itemCode}</span>
                        <span className="tabular-nums text-muted-foreground">{l.qty}</span>
                        <span className="text-info">{l.batch}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {sending.length > 0 && !mixed && <AboutToPost stats={aboutToPost(sending)} />}
      </aside>

      <div className="post-foot flex justify-end gap-2 border-t border-border bg-card px-5 pb-[18px] pt-3.5">
        <Button variant="outline" className="h-9 text-[12.5px]" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button className="h-9 gap-1.5 text-[12.5px] font-semibold" onClick={() => void submit()} disabled={!canSubmit}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Send className="h-3.5 w-3.5" aria-hidden />}
          Post to NetSuite
        </Button>
      </div>
    </>
  );
}

const lookupWarning = (status: ReleasedStatus) => status === 'failed' || status === 'forbidden';

const STAT_ICON = { list: List, 'clipboard-list': ClipboardList, package: Package } as const;

function AboutToPost({ stats }: { stats: Stat[] }) {
  return (
    <div>
      <div className="mb-[7px] text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">About to post</div>
      <div className="grid grid-cols-3 rounded-xl border border-border bg-card shadow-sm">
        {stats.map((st, i) => {
          const Icon = STAT_ICON[st.icon];
          return (
            <div key={st.label} className={cn('flex min-w-0 flex-col gap-[3px] px-3 py-2.5', i > 0 && 'border-l border-border')}>
              <span className="flex min-w-0 items-center gap-[5px] text-[11px] font-medium text-muted-foreground">
                <Icon className="h-3 w-3 shrink-0" aria-hidden />
                <span className="truncate">{st.label}</span>
              </span>
              <span className="text-[22px] font-bold leading-[1.15] tracking-[-0.01em] tabular-nums">{st.value}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="block min-w-0 cursor-help focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <span className="block truncate text-[10.5px] text-muted-foreground underline decoration-muted-foreground/55 decoration-dotted underline-offset-[3px]">
                      {st.sub}
                    </span>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">{st.tip}</TooltipContent>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Notice({
  tone,
  icon: Icon,
  action,
  children,
}: {
  tone: 'warning' | 'destructive';
  icon: typeof TriangleAlert;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-[9px] rounded-lg border px-[11px] py-[9px]',
        tone === 'warning' ? 'border-warning/35 bg-warning/[0.08]' : 'border-destructive/35 bg-destructive/[0.07]',
      )}
    >
      <Icon className={cn('mt-px h-3.5 w-3.5 shrink-0', tone === 'warning' ? 'text-warning' : 'text-destructive')} aria-hidden />
      <div className={cn('min-w-0 flex-1 text-xs leading-normal', tone === 'warning' ? 'text-warning' : 'text-destructive')}>{children}</div>
      {action}
    </div>
  );
}

function IconButton({ label, onClick, className, children }: { label: string; onClick: () => void; className?: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-[7px] text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

// ── The lines section ─────────────────────────────────────────────────────────────────────────────────────

const COMMIT_NOTE_KEY = 'pw.commitNoteHidden';

function readNoteHidden(): boolean {
  try {
    return localStorage.getItem(COMMIT_NOTE_KEY) === '1';
  } catch {
    return false;
  }
}

interface LinesSectionProps {
  picklist: PicklistDto;
  rows: PostRow[];
  store: StoreApi<PostDialogState>;
  figures: ReadonlyMap<number, RowFigures>;
  lookupFailed: boolean;
  loadingReleased: boolean;
  seeds: Readonly<Record<number, number | null>>;
  onOpenSerials: (row: PostRow) => void;
  onOpenDetails: (row: PostRow, anchor: HTMLElement) => void;
  onPostOnly: (rowIds: readonly number[]) => void;
}

function LinesSection({
  picklist,
  rows,
  store,
  figures,
  lookupFailed,
  loadingReleased,
  seeds,
  onOpenSerials,
  onOpenDetails,
  onPostOnly,
}: LinesSectionProps) {
  const selected = useStore(store, (s) => s.selected);
  const groupBy = useStore(store, (s) => s.groupBy);
  const [query, setQuery] = useState('');
  const [noteHidden, setNoteHidden] = useState(readNoteHidden);

  const needle = query.trim().toLowerCase();
  // The filter narrows the VIEW only: selection, the count and what posts stay keyed to every row.
  const visible = needle ? rows.filter((r) => matchesLineQuery(r, needle)) : rows;
  const selectedRows = rows.filter((r) => selected[r.id]);
  const visibleSelected = visible.filter((r) => selected[r.id]).length;
  const mixed = mixedState(selectedRows);
  const onlyOptions = mixed.mixed
    ? postOnlyOptions(rows, selectedRows, (r) => overBy(r.max, figures.get(r.id)!) > 0)
    : [];
  const note = requestorNote(rows);

  const hideNote = () => {
    try { localStorage.setItem(COMMIT_NOTE_KEY, '1'); } catch { /* per-viewer convenience only */ }
    setNoteHidden(true);
  };
  const showNote = () => {
    try { localStorage.removeItem(COMMIT_NOTE_KEY); } catch { /* per-viewer convenience only */ }
    setNoteHidden(false);
  };

  const groups: { label: string; rows: PostRow[] }[] = [];
  if (groupBy === 'none') {
    groups.push({ label: '', rows: visible });
  } else {
    const byLabel = new Map<string, PostRow[]>();
    for (const r of visible) {
      const label = (groupBy === 'endorser' ? r.requestorFullName : r.departmentName) || 'Unknown';
      byLabel.set(label, [...(byLabel.get(label) ?? []), r]);
    }
    for (const [label, groupRows] of byLabel) groups.push({ label, rows: groupRows });
  }

  const renderRow = (row: PostRow) => (
    <PostLineRow
      key={row.id}
      row={row}
      store={store}
      figures={figures.get(row.id)!}
      lookupFailed={lookupFailed}
      loadingReleased={loadingReleased}
      readingSeed={seeds[row.anchorId] ?? null}
      picklistId={picklist.id}
      picklistCreatedAt={picklist.createdAt}
      onOpenSerials={onOpenSerials}
      onOpenDetails={onOpenDetails}
    />
  );

  return (
    <section aria-label="Lines to post" className="post-lines">
      <div className="post-lines-box">
        <div className="mb-[7px] flex flex-wrap items-center justify-between gap-2.5">
          <span className="inline-flex items-center gap-1.5">
            {/* O-46 (kept): tri-state over the VISIBLE rows. */}
            <Checkbox
              checked={visibleSelected === 0 ? false : visibleSelected === visible.length ? true : 'indeterminate'}
              onCheckedChange={(c) => store.getState().setRows(visible.map((r) => r.id), c === true)}
              aria-label="Select all lines"
              className="h-4 w-4"
            />
            <span className="text-xs font-semibold">
              Lines to post{' '}
              <span className="font-medium text-muted-foreground">· {selectedRows.length} of {rows.length} lines selected</span>
            </span>
            {noteHidden && (
              <IconButton label="What posting does" onClick={showNote} className="h-[22px] w-[22px] rounded-md">
                <Info className="h-[13px] w-[13px]" aria-hidden />
              </IconButton>
            )}
          </span>
          <GroupToggle value={groupBy} onChange={(g) => store.getState().setGroupBy(g)} />
        </div>

        <div className="mb-2 flex items-center gap-2">
          <DebouncedSearchInput
            value={query}
            onValueChange={setQuery}
            delay={150}
            placeholder="Filter lines — item, MRS #, endorser, department, end use"
            ariaLabel="Filter lines by item, MRS number, endorser, department or end use"
            className="min-w-0 flex-1"
            inputClassName="h-8 text-xs md:text-xs"
          />
          {needle && (
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{visible.length} of {rows.length}</span>
          )}
        </div>

        {note && (
          <div className="-mt-0.5 mb-2 flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <Users className="h-[13px] w-[13px] shrink-0" aria-hidden />
            {note}
          </div>
        )}

        <div className="post-table rounded-[9px] border border-border">
          {/* Opaque, and sticky when wide. The colour and the gradient stay TWO classes: one combined arbitrary
              background is dropped whole by the browser (UI-GUIDELINES §9). */}
          <div className="post-row-grid post-head-grid post-table-head top-0 z-[3] h-[34px] items-center border-b border-border bg-card bg-[linear-gradient(hsl(var(--muted)/0.4),hsl(var(--muted)/0.4))] px-[13px] text-[11px] font-medium text-muted-foreground">
            <span />
            <span>Item</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="cursor-help focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">For MRS</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                The MRS line the picker recorded these units for, and who asked for it. Each row becomes one MIS line.
              </TooltipContent>
            </Tooltip>
            <span className="text-right">To post</span>
            <span>Reading</span>
            <span>Memo</span>
          </div>

          {visible.length === 0 ? (
            <EmptyState
              size="compact"
              icon={SearchX}
              title={needle ? `No lines match “${query.trim()}”.` : 'Nothing picked is left to post.'}
              action={needle ? (
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setQuery('')}>
                  Clear filter
                </Button>
              ) : undefined}
            />
          ) : (
            groups.map((g) => (
              <div key={g.label || 'all'}>
                {g.label && (
                  <GroupHeader
                    label={g.label}
                    rows={g.rows}
                    selectedCount={g.rows.filter((r) => selected[r.id]).length}
                    onToggle={(on) => store.getState().setRows(g.rows.map((r) => r.id), on)}
                  />
                )}
                {g.rows.map(renderRow)}
              </div>
            ))
          )}
        </div>
      </div>

      {mixed.mixed && (
        <div className="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive/[0.08] px-[11px] py-[9px] text-xs text-destructive">
          <div className="flex items-start gap-[7px]">
            <Users className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="leading-[1.45]">{mixed.message}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 pl-[21px]">
            {onlyOptions.map((o) => (
              <Button
                key={o.key}
                variant="outline"
                size="sm"
                onClick={() => onPostOnly(o.rowIds)}
                className="h-7 gap-1.5 bg-card px-2.5 text-xs font-medium text-foreground"
              >
                <UserRound className="h-[13px] w-[13px] text-muted-foreground" aria-hidden />
                Post only {o.name}
                <span className="tabular-nums text-muted-foreground">· {o.count}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {!noteHidden && (
        <div className="flex shrink-0 items-start gap-2 rounded-lg border border-info/30 bg-info/[0.08] py-[7px] pl-[11px] pr-[7px]">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-info" aria-hidden />
          <p className="min-w-0 flex-1 text-xs leading-normal text-info">
            Posting is a pure commit — it records what's already picked and moves NetSuite inventory. It never edits the
            record: wrong quantity or serial? Close this and use <b>Adjust</b> on the item.
          </p>
          <IconButton label="Hide this note" onClick={hideNote} className="h-6 w-6 rounded-md text-info hover:bg-info/[0.12]">
            <X className="h-3.5 w-3.5" aria-hidden />
          </IconButton>
        </div>
      )}
    </section>
  );
}

function GroupToggle({ value, onChange }: { value: GroupBy; onChange: (g: GroupBy) => void }) {
  const options: [GroupBy, string][] = [['none', 'No grouping'], ['endorser', 'By endorser'], ['department', 'By department']];
  return (
    <div className="ml-auto inline-flex gap-0.5 rounded-lg bg-muted p-0.5" role="group" aria-label="Group lines">
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={cn(
            'h-[26px] rounded-md px-2.5 text-[11.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            value === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function GroupHeader({
  label,
  rows,
  selectedCount,
  onToggle,
}: {
  label: string;
  rows: PostRow[];
  selectedCount: number;
  onToggle: (on: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-[9px] border-b border-border/60 bg-muted/35 px-[13px] py-[7px]">
      <Checkbox
        checked={selectedCount === 0 ? false : selectedCount === rows.length ? true : 'indeterminate'}
        onCheckedChange={(c) => onToggle(c === true)}
        aria-label={`Select all in ${label}`}
        className="h-[15px] w-[15px] rounded-[4px]"
      />
      <span className="text-xs font-semibold">{label}</span>
      <span className="text-[11px] text-muted-foreground">{rows.length} {rows.length === 1 ? 'line' : 'lines'}</span>
    </div>
  );
}
